import { prefersReducedMotion, onDocumentReady } from '@theme/utilities';

(() => {
  "use strict";

  /* ═══════════════════════════════════════════════════════════════════════════
     CUSTOM QUICK ADD VARIANT PICKER
     Scrolls media column to top when color variant changes in quick-add modals
     ═══════════════════════════════════════════════════════════════════════════ */

  /* ═══════════════════════════════════════════════════════════════════════════
     CONFIG
     ═══════════════════════════════════════════════════════════════════════════ */
  const CONFIG = {
    selectors: {
      horizonDialog: 'dialog.quick-add-modal',
      customDialog: 'dialog.custom-variant-quick-add-modal',
      variantPicker: 'variant-picker',
      mediaContainer: '.product-information__media',
    },
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     DOM OPERATIONS
     ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Scrolls media container to top with motion preference support
   * @param {Element} container - The dialog container
   */
  const scrollToTop = (container) => {
    const mediaContainer = container.querySelector(CONFIG.selectors.mediaContainer);
    if (!mediaContainer) return;

    mediaContainer.scrollTo({
      top: 0,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     EVENT HANDLERS
     ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Handles variant change events in quick-add modals
   * @param {Event} event - The change event
   */
  const handleVariantChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const isColorChange = target.name?.includes('Color');
    if (!isColorChange) return;

    const dialog = target.closest(
      `${CONFIG.selectors.horizonDialog}, ${CONFIG.selectors.customDialog}`
    );
    if (dialog) {
      scrollToTop(dialog);
    }
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     INITIALIZATION
     ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Initializes scroll behavior for a dialog's variant picker
   * @param {Element} dialog - The dialog element
   */
  const initializeScrollBehavior = (dialog) => {
    const variantPicker = dialog.querySelector(CONFIG.selectors.variantPicker);
    if (!variantPicker) return;

    variantPicker.addEventListener('change', handleVariantChange);
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     OBSERVERS
     ═══════════════════════════════════════════════════════════════════════════ */

  /**
   * Observes dialog open state to initialize behavior
   * @param {Element} dialog - The dialog element
   */
  const observeDialogOpen = (dialog) => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'open') continue;

        if (dialog.hasAttribute('open')) {
          initializeScrollBehavior(dialog);
        }
      }
    });

    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  };

  /* ═══════════════════════════════════════════════════════════════════════════
     MAIN EXECUTION
     ═══════════════════════════════════════════════════════════════════════════ */
  onDocumentReady(() => {
    const horizonDialog = document.querySelector(CONFIG.selectors.horizonDialog);
    if (horizonDialog) {
      observeDialogOpen(horizonDialog);
    }

    const customDialog = document.querySelector(CONFIG.selectors.customDialog);
    if (customDialog) {
      observeDialogOpen(customDialog);
    }
  });
})();
